// src/components/layouts/app/header/desktop/CountryDropdown.tsx
import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, MapPin, Check } from 'lucide-react';
import { SearchableList } from '../../../../ui/elements/SearchableList';

interface Country {
  code: string;
  name: string;
  flagUrl: string;
}

const countries: Country[] = [
  { code: 'BJ', name: 'Bénin', flagUrl: 'https://flagcdn.com/w160/bj.png' },
  { code: 'CI', name: 'Côte d\'Ivoire', flagUrl: 'https://flagcdn.com/w160/ci.png' },
  { code: 'SN', name: 'Sénégal', flagUrl: 'https://flagcdn.com/w160/sn.png' },
  { code: 'FR', name: 'France', flagUrl: 'https://flagcdn.com/w160/fr.png' },
];

interface CountryDropdownProps {
  selectedCode?: string;
  onSelect?: (code: string) => void;
}

export const CountryDropdown: React.FC<CountryDropdownProps> = ({ selectedCode = 'BJ', onSelect }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<number | undefined>(undefined);

  const selectedCountry = countries.find(c => c.code === selectedCode) || countries[0];

  const handleMouseEnter = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setIsOpen(true);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = window.setTimeout(() => {
      setIsOpen(false);
    }, 150);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const handleSelect = (country: Country) => {
    onSelect?.(country.code);
    setIsOpen(false);
  };

  const searchableItems = countries.map(country => ({
    id: country.code,
    label: `${country.name} (${country.code})`,
    original: country,
  }));

  return (
    <div
      className="relative"
      ref={dropdownRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-2 py-1 rounded-md hover:bg-gray-100 transition-colors duration-200"
      >
        <MapPin className="w-4 h-4 text-black" />
        <div className="flex items-center gap-1.5">
          <span className="text-sm text-black">Livraison à</span>
          <strong className="text-sm text-black">{selectedCode}</strong>
          <img
            src={selectedCountry.flagUrl}
            alt={selectedCountry.name}
            className="w-5 h-3.5 object-cover rounded-sm shadow-sm"
          />
        </div>
        <ChevronDown className={`w-4 h-4 text-black transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      <div
        className={`
          absolute top-full left-0 mt-2 w-64 bg-white border border-gray-300 rounded-xl shadow-lg z-20 overflow-hidden
          transition-all duration-300 ease-out origin-top
          ${isOpen
            ? 'opacity-100 scale-y-100 visible pointer-events-auto'
            : 'opacity-0 scale-y-0 invisible pointer-events-none'
          }
        `}
      >
        <SearchableList
          items={searchableItems}
          selectedId={selectedCode}
          onSelect={(item) => handleSelect(item.original)}
          searchPlaceholder="Rechercher un pays..."
          emptyMessage="Aucun résultat"
          renderItem={(item, isSelected) => (
            <button
              className={`w-full text-left px-4 py-2.5 flex items-center justify-between transition-colors ${isSelected ? 'bg-primary/10 text-primary font-medium' : 'text-black hover:bg-gray-100'
                }`}
            >
              <div className="flex items-center gap-3">
                <img src={item.original.flagUrl} alt={item.original.name} className="w-6 h-4 object-cover rounded-sm shadow-sm" />
                <span className="text-sm">{item.original.name}</span>
                <span className="text-xs text-gray-500">({item.original.code})</span>
              </div>
              {isSelected && <Check className="w-4 h-4 text-primary" />}
            </button>
          )}
        />
      </div>
    </div>
  );
};